import React from "react";
import styled from "styled-components";
import type { ArtistArtwork } from "../types";

interface Props {
  artwork: ArtistArtwork;
  pictureUrl: string;
  onClick?: () => void;
}

/** Plain card — minimal chrome, no badges or heavy frames. */
export const DefaultTheme: React.FC<Props> = ({ artwork, pictureUrl, onClick }) => {
  const dateMadeLabel = artwork.dateMade ? galleryDateStamp(artwork.dateMade) : "";

  const artistLine = `${artwork.artist.userEmoji ? `${artwork.artist.userEmoji} ` : ""}${
    artwork.artist.displayName || artwork.artist.username
  }`;

  const wb = artwork.wayback;
  const wbCapturedDate = wb?.capturedAt
    ? new Date(wb.capturedAt).toISOString().slice(0, 10)
    : "";

  return (
    <Container onClick={onClick} $interactive={Boolean(onClick)}>
      <Frame>
        <TopRow>
          <DateStyle>{dateMadeLabel}</DateStyle>
          <MetaCol>
            <CreatorStyle>by {artistLine}</CreatorStyle>
            {artwork.status === "pending" && <QuietLine>pending</QuietLine>}
            {artwork.status === "rejected" && <QuietLine>rejected</QuietLine>}
            {artwork.donation && <QuietLine>donation</QuietLine>}
          </MetaCol>
        </TopRow>

        <Img src={pictureUrl} alt={artwork.title} loading="lazy" />

        <FooterBlock>
          <Title>{artwork.title}</Title>
          {(wb?.status === "queued" || wb?.status === "requested") && (
            <QuietLine title="archive.org snapshot queued (~45min after approval)">
              wayback pending
            </QuietLine>
          )}
          {wb?.status === "captured" && wb.snapshotUrl && (
            <PlainArchiveLink
              href={wb.snapshotUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              archived {wbCapturedDate}
            </PlainArchiveLink>
          )}
          {wb?.status === "failed" && (
            <QuietLine title={wb.error || "wayback failed"}>wayback failed</QuietLine>
          )}
        </FooterBlock>
      </Frame>
    </Container>
  );
};

/** Same stamping idea as legacy `gallery_item.js` (`4_aug_21`). */
function galleryDateStamp(isoLike: string): string {
  const date = new Date(isoLike);
  if (Number.isNaN(date.getTime())) return "";
  let month = date.toLocaleString("default", { month: "short" }).toLowerCase();
  const day = date.getDate();
  const yearShort = date.getFullYear().toString().slice(-2);
  if (["a", "s", "o"].includes(month[1] ?? "")) {
    month =
      month[0] +
      (month[1] === "a" ? "4" : month[1] === "s" ? "5" : "0") +
      (month[2] ?? "");
  }
  return `${day}_${month}_${yearShort}`;
}

const Container = styled.div<{ $interactive?: boolean }>`
  position: relative;
  cursor: ${(p) => (p.$interactive ? "pointer" : "default")};
`;

const Frame = styled.div`
  box-sizing: border-box;
  display: inline-block;
  vertical-align: top;
  width: 17rem;
  max-width: 100%;
  background: #ffffff;
  border: 1px solid #000000;
  padding: 8px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const MetaCol = styled.div`
  text-align: right;
  flex: 1;
  min-width: 0;
`;

const DateStyle = styled.span`
  font-size: 12px;
  font-weight: 600;
`;

const CreatorStyle = styled.div`
  font-size: 12px;
  font-weight: 600;
`;

const QuietLine = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #444444;
`;

const Img = styled.img`
  display: block;
  width: 100%;
  height: auto;
  max-height: 280px;
  margin-top: 10px;
  object-fit: contain;
`;

const FooterBlock = styled.div`
  margin-top: 8px;
`;

const Title = styled.div`
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.35;
`;

const PlainArchiveLink = styled.a`
  display: inline-block;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 500;
  color: inherit;
  text-decoration: underline;

  &:hover {
    text-decoration: underline;
  }
`;

export default DefaultTheme;
